import JSZip from 'jszip'
import { BookletFolder, sanitizeFileSystemName } from '../../../utils/bookletFolderUtils'

interface CopyZipOptions {
  bookletFolders?: BookletFolder[]
  duplicateToEachBooklet?: boolean
}

const getMatchingBookletFolder = (
  filePath: string,
  bookletFolders: BookletFolder[]
): BookletFolder | undefined => {
  const baseFileName = sanitizeFileSystemName(filePath.split('/').pop() ?? filePath)
  return bookletFolders.find(({ title }) => baseFileName.startsWith(`${sanitizeFileSystemName(title)}`))
}

/**
 * Copies files from a source ZIP into the target ZIP and optionally nests them under booklet-specific folders.
 * When duplicateToEachBooklet is true, every file will be copied into each booklet folder.
 */
export const copyZipFilesToMaster = async (
  sourceZip: JSZip,
  targetZip: JSZip,
  folderPrefix = '',
  options: CopyZipOptions = {}
): Promise<void> => {
  const { bookletFolders = [], duplicateToEachBooklet = false } = options

  await Promise.all(
    Object.keys(sourceZip.files).map(async (filePath) => {
      const file = sourceZip.files[filePath]
      if (!file || file.dir) {
        return
      }

      const fileBlob = await file.async('blob')
      const normalizedPrefix = folderPrefix ? `${folderPrefix}` : ''

      if (duplicateToEachBooklet && bookletFolders.length > 0) {
        bookletFolders.forEach(({ folderName }) => {
          const targetPath = `${folderName}/${normalizedPrefix}${filePath}`
          targetZip.file(targetPath, fileBlob)
        })
        return
      }

      if (bookletFolders.length > 0) {
        const matchingFolder = getMatchingBookletFolder(filePath, bookletFolders)
        if (matchingFolder) {
          const targetPath = `${matchingFolder.folderName}/${normalizedPrefix}${filePath}`
          targetZip.file(targetPath, fileBlob)
          return
        }
      }

      const targetPath = normalizedPrefix ? `${normalizedPrefix}${filePath}` : filePath
      targetZip.file(targetPath, fileBlob)
    })
  )
}

